import { Slot } from "@radix-ui/react-slot";
import { type VariantProps, cva } from "class-variance-authority";
import type * as React from "react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  `
    inline-flex items-center justify-center shrink-0
    whitespace-nowrap font-medium
    rounded-full cursor-pointer transition-all
    active:opacity-60
    focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring
    disabled:pointer-events-none disabled:opacity-50
    aria-invalid:ring-2 aria-invalid:ring-destructive/20 aria-invalid:border-destructive dark:aria-invalid:ring-destructive/40
    [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-5
  `,
  {
    variants: {
      variant: {
        default:
          "bg-foreground text-primary-foreground shadow-xs hover:brightness-110",
        destructive:
          "bg-destructive text-white shadow-xs hover:brightness-110 focus-visible:outline-destructive dark:bg-destructive/60",
        outline:
          "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        secondary:
          "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        brand:
          "bg-brand text-primary-foreground-light shadow-xs hover:brightness-110",
      },
      size: {
        default: "h-9 px-4 py-2 gap-2 text-sm",
        sm: "h-8 rounded-full gap-1.5 px-3 text-sm has-[>svg]:px-2.5 [&_svg:not([class*='size-'])]:size-4",
        lg: "h-10 rounded-full px-6 gap-2 text-sm has-[>svg]:px-4 [&_svg:not([class*='size-'])]:size-5",
        xl: "h-12 rounded-full px-9 gap-2 text-base has-[>svg]:px-5 [&_svg:not([class*='size-'])]:size-5",
        full: "w-full h-12 rounded-full gap-2",
        icon: "size-9 rounded-full",
        "icon-sm": "size-8 rounded-full",
        "icon-lg": "size-10 rounded-full",
        "icon-xl": "size-12 rounded-full",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
